/**
 * Per-specialty error boundary.
 *
 * rev v11.0.0 · Phase 2 / C3
 *
 * A specialty that breaks must not take the application with it, and it must
 * not leave the clinician stranded in a half-rendered workspace.
 *
 * This boundary is scoped to ONE specialty. When it catches, it offers the
 * rollback: go back to the legacy interface for that specialty. The action
 * is immediate and local — it does not need the API, because the legacy
 * interface is always reachable.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { captureError } from "@/lib/sentry";
import { ROUTES, asRoutePath } from "@/lib/routes";

interface Props {
  children: ReactNode;
  /** The canonical slug of the specialty this boundary protects. */
  specialtyId: string;
  /** The label of the specialty, shown in the message. */
  specialtyLabel?: string;
  /** Where to send the clinician when they ask for the legacy interface. */
  legacyHref?: string;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class SpecialtyErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info });
    captureError(error, {
      route: `/clinical/${this.props.specialtyId}`,
      componentStack: info.componentStack?.slice(0, 2000),
      reactError: true,
      specialty: this.props.specialtyId,
    });
  }

  render() {
    const { error, info } = this.state;
    const { children, specialtyId, specialtyLabel, legacyHref } = this.props;

    if (!error) return children;

    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6" data-specialty={specialtyId}>
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              L'espace {specialtyLabel ?? specialtyId} n'a pas pu s'afficher
            </CardTitle>
            <CardDescription>
              L'erreur a été reportée. Vous pouvez revenir à l'interface
              habituelle : aucune donnée n'a été perdue.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <code className="block rounded bg-muted p-2 text-left text-xs text-destructive">
              {error.message}
            </code>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" asChild>
                <Link to={asRoutePath(legacyHref ?? ROUTES.home)}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Revenir à l'interface habituelle
                </Link>
              </Button>
              <Button
                variant="ghost"
                onClick={() => this.setState({ error: null, info: null })}
              >
                Réessayer
              </Button>
            </div>
            {info?.componentStack && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Détails techniques</summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-muted p-2">
                  {info.componentStack}
                </pre>
              </details>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
}
